import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELD_LABEL = {
  title: "Product name", brand: "Brand", description: "Description", selling_price: "Selling price",
  mrp: "MRP", gst_rate: "GST rate", images: "Images", colour_images: "Images by colour",
  video_url: "Video", sizes: "Sizes", colors: "Colours", variants: "Variants", material: "Material",
  care: "Care instructions", highlights: "Highlights", attributes: "Attributes", department: "Department",
  category_id: "Category", subcategory_id: "Subcategory", country_of_origin: "Country of origin",
  manufacturer: "Manufacturer",
};
const PRICE_FIELDS = ["selling_price", "mrp", "price"];

function Thumbs({ list }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {(list || []).slice(0, 6).map((src, i) => (
        <div key={i} className="w-12 h-12 bg-secondary overflow-hidden">{src && <img src={src} alt="" className="w-full h-full object-cover" />}</div>
      ))}
    </div>
  );
}

function Value({ field, val }) {
  const empty = val == null || val === "" || (Array.isArray(val) && val.length === 0) ||
    (val && typeof val === "object" && !Array.isArray(val) && Object.keys(val).length === 0);
  if (empty) return <span className="text-muted-foreground italic text-sm">empty</span>;
  if (PRICE_FIELDS.includes(field)) return <span className="font-mono text-sm">₹{Number(val).toLocaleString("en-IN")}</span>;
  if (field === "gst_rate") return <span className="font-mono text-sm">{val}%</span>;
  if (field === "images") return <Thumbs list={val} />;
  if (field === "colour_images") return (
    <div className="space-y-1.5">
      {Object.entries(val).map(([c, imgs]) => (
        <div key={c}><p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{c}</p><Thumbs list={imgs} /></div>
      ))}
    </div>
  );
  if (field === "variants") return <span className="text-sm">{val.length} variant{val.length !== 1 ? "s" : ""}</span>;
  if (Array.isArray(val)) return <span className="text-sm">{val.join(", ")}</span>;
  if (typeof val === "object") return <span className="text-xs font-mono">{Object.entries(val).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join("/") : v}`).join(" · ")}</span>;
  return <span className="text-sm whitespace-pre-wrap">{String(val)}</span>;
}

export default function ChangeReview({ cr, onClose, onDone }) {
  const changes = cr.changes || [];
  const [dec, setDec] = useState(Object.fromEntries(changes.map((c) => [c.field, "approve"])));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const approved = Object.values(dec).filter((d) => d === "approve").length;

  const setAll = (v) => setDec(Object.fromEntries(changes.map((c) => [c.field, v])));

  const apply = async () => {
    setBusy(true);
    try {
      const decisions = changes.map((c) => ({ field: c.field, decision: dec[c.field] || "approve" }));
      await api.post(`/admin/change-requests/${cr.id}/decide`, { decisions, note });
      toast.success(approved === 0 ? "Change rejected" : approved === changes.length ? "Changes applied" : "Selected changes applied");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-foreground/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border-2 border-foreground w-full max-w-3xl max-h-[86vh] flex flex-col shadow-[6px_6px_0_hsl(var(--foreground))]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-foreground flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{cr.vendor_name} · change request</p>
            <h3 className="font-serif text-2xl tracking-tight truncate">{cr.product_title}</h3>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setAll("approve")} className="font-mono text-[10px] uppercase tracking-widest border border-foreground/30 px-2 py-1 hover:bg-foreground hover:text-background">Approve all</button>
            <button onClick={() => setAll("reject")} className="font-mono text-[10px] uppercase tracking-widest border border-foreground/30 px-2 py-1 hover:bg-destructive hover:text-destructive-foreground">Reject all</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {changes.map((c) => (
            <div key={c.field} className="border border-foreground/25">
              <div className="flex items-center justify-between px-4 py-2 border-b border-foreground/15 bg-[hsl(var(--muted))]">
                <p className="font-mono text-[11px] uppercase tracking-widest">{FIELD_LABEL[c.field] || c.field.replace(/_/g, " ")}</p>
                <div className="flex gap-1">
                  <button onClick={() => setDec((d) => ({ ...d, [c.field]: "approve" }))}
                    className={`px-2 py-1 text-[10px] font-mono uppercase ${dec[c.field] === "approve" ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "border border-foreground/30"}`}>Approve</button>
                  <button onClick={() => setDec((d) => ({ ...d, [c.field]: "reject" }))}
                    className={`px-2 py-1 text-[10px] font-mono uppercase ${dec[c.field] === "reject" ? "bg-destructive text-destructive-foreground" : "border border-foreground/30"}`}>Reject</button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-4">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Current (live)</p>
                  <Value field={c.field} val={c.from} />
                </div>
                <div className="md:border-l md:border-foreground/15 md:pl-4">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[hsl(var(--accent))] mb-1">Proposed</p>
                  <Value field={c.field} val={c.to} />
                </div>
              </div>
            </div>
          ))}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Note (optional — recorded in the audit log)</p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full border border-foreground p-2 text-sm min-h-[60px] outline-none focus:border-[hsl(var(--accent))]" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-foreground flex items-center justify-between gap-4">
          <p className="font-mono text-[11px] text-muted-foreground">{approved} approve · {changes.length - approved} reject</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="border border-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">Cancel</button>
            <button disabled={busy} onClick={apply} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-6 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">
              {busy ? "Applying…" : "Apply decisions"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
